import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import {
  FaCreditCard,
  FaFileAlt,
  FaHome,
  FaReceipt,
  FaShieldAlt,
  FaSignOutAlt,
  FaTrash,
  FaUser,
} from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../config/axiosConfig';
import { ROUTES } from '../config/appConfig';
import BrandLogo from '../components/BrandLogo';
import DeliveryForm from '../components/DeliveryForm';
import DeliveryList from '../components/DeliveryList';
import EmailVerificationPrompt from '../components/auth/EmailVerificationPrompt';
import { logoutUser, refreshCurrentUser, updateUserProfile } from '../store/slices/authSlice';
import { getErrorMessage } from '../utils/apiResponseUtils';
import { readPayload } from '../utils/dataUtils';
import CustomerWallet from './CustomerWallet';
import SavedAddresses from '../components/SavedAddresses';
import CustomerHomeContent from '../components/CustomerHomeContent';
import useCommerceScope from '../utils/useCommerceScope';
import {validateWallet} from '../utils/commerceValidation';
import { money } from '../utils/commerce';
import '../styles/CustomerDashboard.css';

const tabs = [
  { key: 'home', label: 'Home', icon: FaHome },
  { key: 'orders', label: 'Orders', icon: FaReceipt },
  { key: 'payments', label: 'Payments', icon: FaCreditCard },
  { key: 'account', label: 'Account', icon: FaUser },
];

const getTabFromPath = (pathname) => {
  if (pathname === '/dashboard/account')return 'account';
  if (pathname === ROUTES.orders) {
    return 'orders';
  }

  return null;
};

const CustomerDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const {current}=useCommerceScope(user);
  const [activeTab, setActiveTab] = useState(getTabFromPath(location.pathname) || 'home');
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profile, setProfile] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [legalContent, setLegalContent] = useState(null);
  const [showLogout, setShowLogout] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showBooking, setShowBooking] = useState(true);
  const [profileForm, setProfileForm] = useState({ firstName: '', lastName: '', email: '' });
  const [savedLandingFlow,setSavedLandingFlow]=useState(false);
  useEffect(()=>{try{setSavedLandingFlow(window.sessionStorage.getItem('landingBookingFlow')==='true');}catch{setSavedLandingFlow(false);}},[]);
  const cameFromLandingFlow=location.state?.fromLandingFlow===true||savedLandingFlow;
  const requiresEmailVerification = user?.emailVerificationRequired === true;
  const canCreateOrder = !requiresEmailVerification && user?.phoneVerificationRequired !== true;
  const accountEmail = profile?.email || user?.email || profileForm.email || '';

  const loadCustomerData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const requests = [axiosInstance.get('/account/profile')];
      if (activeTab !== 'orders') {
        requests.push(
          axiosInstance.get('/payment/wallet')
        );
      }
      const [profileResult, walletResult] = await Promise.allSettled(requests);
      if(!current())return;
      if (profileResult.status === 'fulfilled') {
        const nextProfile = readPayload(profileResult.value);
        setProfile(nextProfile);
        dispatch(updateUserProfile(nextProfile));
        setProfileForm({
          firstName: nextProfile.firstName || '',
          lastName: nextProfile.lastName || '',
          email: nextProfile.email || '',
        });
      }
      if (walletResult?.status === 'fulfilled') setWallet(validateWallet(readPayload(walletResult.value)));else if(walletResult)setWallet(null);
      const failure = [profileResult, walletResult].find((result) => result?.status === 'rejected');
      if (failure) throw failure.reason;
    } catch (apiError) {
      if(current()){setWallet(null);setError(getErrorMessage(apiError, 'Unable to load customer dashboard right now.'));}
    } finally {
      if(current())setLoading(false);
    }
  }, [activeTab, dispatch,current]);

  useEffect(() => {
    loadCustomerData();
  }, [loadCustomerData]);

  useEffect(() => {
    if (location.state?.paymentError) {
      setError(location.state.paymentError);
    }

    const pathTab = getTabFromPath(location.pathname);

    if (pathTab) {
      setActiveTab(pathTab);
      return;
    }

    const focusSection = location.state?.focusSection;

    if (focusSection === 'orders' || focusSection === 'history') {
      setActiveTab('orders');
      return;
    }

    if (focusSection === 'payments' || focusSection === 'account' || focusSection === 'home') {
      setActiveTab(focusSection);
      return;
    }

    if (focusSection === 'create-delivery') {
      setActiveTab('home');
      setShowBooking(true);
    }
  }, [location.pathname, location.state]);

  const runAction = async (action, message) => {
    setWorking(true);
    setError('');
    setSuccess('');
    try {
      await action();
      if (message) setSuccess(message);
    } catch (apiError) {
      setError(getErrorMessage(apiError, 'Something went wrong. Please try again.'));
    } finally {
      setWorking(false);
    }
  };

  const saveProfile = (event) => {
    event.preventDefault();
    runAction(async () => {
      const response = await axiosInstance.put('/account/profile', profileForm);
      const nextProfile = readPayload(response);
      setProfile(nextProfile);
      dispatch(updateUserProfile(nextProfile));
    }, 'Profile updated.');
  };

  const openLegal = (type) => {
    runAction(async () => {
      const response = await axiosInstance.get(type === 'terms' ? '/content/terms' : '/content/privacy-policy');
      const payload = readPayload(response);
      const text = typeof response.data?.data === 'string' ? response.data.data : payload.content || payload.body || payload.text;
      setLegalContent({ type, data: { content: text } });
    });
  };

  const handleLogout = () => {
    runAction(async () => {
      await dispatch(logoutUser());
      navigate(ROUTES.login);
    });
  };

  const handleDeleteAccount = () => navigate('/dashboard/account-closure');

  const handleEmailVerified = () => {
    setProfile((currentProfile) => (
      currentProfile ? { ...currentProfile, emailVerified: true } : currentProfile
    ));
    dispatch(refreshCurrentUser());
    loadCustomerData();
    setSuccess('Email verified. You can continue.');
  };

  const renderHome = () => (
    <div className="customer-grid">
      <section className="customer-hero">
        <div>
          <span className="customer-kicker">Customer Home</span>
          <h1>Book the right RedGaady service for your pickup.</h1>
          <p>
            {cameFromLandingFlow
              ? 'Your landing page route is ready. Complete parcel and contact details to continue.'
              : 'Enter pickup, drop-off, parcel, and contact details to create your order.'}
          </p>
        </div>
        <div className="wallet-mini">
          <span>Wallet Credits</span>
          <strong>{money(wallet?.balance)}</strong>
        </div>
      </section>

      <CustomerHomeContent />

      {showBooking && !canCreateOrder && (
        <section className="customer-panel">
          <div className="panel-title-row">
            <div>
              <span className="customer-kicker">Email Verification</span>
              <h2>Verify email to create orders</h2>
            </div>
          </div>
          <EmailVerificationPrompt
            email={accountEmail}
            description="Please verify your email before creating an order."
            onVerified={handleEmailVerified}
          />
        </section>
      )}

      {showBooking && canCreateOrder && (
        <section className="customer-panel">
          <div className="panel-title-row">
            <div>
              <span className="customer-kicker">Booking</span>
              <h2>Complete Delivery Details</h2>
            </div>
          </div>
          <DeliveryForm accountProfile={profile} />
        </section>
      )}
    </div>
  );

  const renderOrders = () => (
    <section className="customer-panel">
      <div className="panel-title-row">
        <div>
          <span className="customer-kicker">Orders</span>
          <h2>Your Delivery Timeline</h2>
        </div>
      </div>
      <DeliveryList />
    </section>
  );

  const renderPayments = () => <CustomerWallet />;

  const renderAccount = () => (
    <div className="customer-grid">
      <section className="customer-panel">
        <div className="panel-title-row">
          <div>
            <span className="customer-kicker">Profile</span>
            <h2>Account Details</h2>
          </div>
        </div>
        <Form onSubmit={saveProfile} className="account-form">
          <Form.Control placeholder="First name" value={profileForm.firstName} onChange={(event) => setProfileForm({ ...profileForm, firstName: event.target.value })} />
          <Form.Control placeholder="Last name" value={profileForm.lastName} onChange={(event) => setProfileForm({ ...profileForm, lastName: event.target.value })} />
          <Form.Control placeholder="Email" value={profileForm.email} onChange={(event) => setProfileForm({ ...profileForm, email: event.target.value })} />
          <Form.Control value={profile?.phone || user?.phone || ''} disabled aria-label="Phone number" />
          <Button type="submit" disabled={working}>Save Profile</Button>
        </Form>
        <div className="verified-row">
          <FaShieldAlt />
          <span>{profile?.emailVerified ? 'Email verified' : 'Email not verified'} - Phone cannot be edited</span>
        </div>
        {requiresEmailVerification && (
          <div className="mt-3">
            <EmailVerificationPrompt
              email={accountEmail}
              description="Please verify your email before creating an order."
              onVerified={handleEmailVerified}
            />
          </div>
        )}
      </section>

      <section className="customer-panel">
        <SavedAddresses />
        <div className="account-actions">
          <Button variant="outline-primary" onClick={() => openLegal('terms')}><FaFileAlt /> Terms</Button>
          <Button variant="outline-primary" onClick={() => openLegal('privacy')}><FaShieldAlt /> Privacy</Button>
          <Button
            variant="outline-secondary"
            className="mobile-account-logout"
            onClick={() => setShowLogout(true)}
          >
            <FaSignOutAlt /> Logout
          </Button>
          <Button variant="outline-danger" onClick={() => setShowDelete(true)}><FaTrash /> Delete Account</Button>
        </div>
      </section>
    </div>
  );

  return (
    <div className="customer-app-shell">
      <aside className="customer-sidebar">
        <div className="customer-brand-row">
          <BrandLogo />
          <div>
            <strong>RedGaady</strong>
            <span>Customer</span>
          </div>
        </div>
        <nav>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                type="button"
                className={activeTab === tab.key ? 'active' : ''}
                onClick={() => setActiveTab(tab.key)}
              >
                <Icon /> {tab.label}
              </button>
            );
          })}
        </nav>
        <button
          type="button"
          className="customer-sidebar-logout"
          onClick={() => setShowLogout(true)}
        >
          <FaSignOutAlt /> Logout
        </button>
      </aside>

      <main className="customer-main">
        <header className="customer-topbar">
          <div>
            <span className="customer-kicker">Welcome</span>
            <h1>{profile?.firstName || user?.firstName || 'Customer'}</h1>
          </div>
        </header>
        <p><Link to="/dashboard/support">Help &amp; Support — submit or track a ticket</Link> · <a href="mailto:redgaady@gmail.com">redgaady@gmail.com</a></p>

        <p><Link to="/dashboard/notifications">Email preferences and history</Link></p>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {loading ? (
          <div className="customer-loading"><Spinner animation="border" /> Loading RedGaady...</div>
        ) : (
          <>
            {activeTab === 'home' && renderHome()}
            {activeTab === 'orders' && renderOrders()}
            {activeTab === 'payments' && renderPayments()}
            {activeTab === 'account' && renderAccount()}
          </>
        )}
      </main>

      <nav className="customer-bottom-tabs">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              type="button"
              className={activeTab === tab.key ? 'active' : ''}
              onClick={() => setActiveTab(tab.key)}
            >
              <Icon />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </nav>

      <Modal show={!!legalContent} onHide={() => setLegalContent(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{legalContent?.type === 'terms' ? 'Terms' : 'Privacy Policy'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="legal-copy">
            {legalContent?.data?.content || legalContent?.data?.body || legalContent?.data?.text || 'Legal content is not available.'}
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showLogout} onHide={() => setShowLogout(false)} centered>
        <Modal.Header closeButton><Modal.Title>Logout?</Modal.Title></Modal.Header>
        <Modal.Body>Confirm logout from this RedGaady session.</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowLogout(false)}>Cancel</Button>
          <Button onClick={handleLogout} disabled={working}>Logout</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
        <Modal.Header closeButton><Modal.Title>Delete account?</Modal.Title></Modal.Header>
        <Modal.Body>Review your wallet balance, outstanding dues, pending refunds and closure terms before closing your account.</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDelete(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleDeleteAccount} disabled={working}>Review account closure</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CustomerDashboard;
